import { DownloadOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Modal, Table, Tag, Input } from 'antd';
import {
  SOLICITUD_TIPOS,
  SOLICITUD_TIPOS_ARRAY,
  getColorForStatus,
} from 'assets/data/SolicitudesEstados';
import { useCalendarActions } from 'providers/CalendarSolc/CalendarActionsProvider';
import { useSolicitudesMantencion } from 'providers/SolicitudesMantencionProvider';
import { useSolicitudes } from 'providers/SolicitudesProvider';
import React, { useEffect, useMemo, useState } from 'react';

// Campo que la tabla usa como clave de fila. Tiene que ser único y no nulo en
// TODA solicitud: React coacciona la clave a texto, así que un campo nulo deja a
// todas las filas compartiendo la clave `"null"` y React puede reutilizar la fila
// equivocada al filtrar/paginar. Lo cubre `src/api/solicitudes.contract.test.js`
// contra la API real.
export const SOLICITUDES_ROW_KEY = 'id';

const SolicitudesTable = () => {
  const { data, hasCalled, call, isLoading, addSolicitudToDay } = useSolicitudes();
  const { openDowloadMantencion } = useSolicitudesMantencion();
  const { selectedDay } = useCalendarActions();

  const handleClickInSolc = (record) => {
    if ([SOLICITUD_TIPOS.ENVIADA, SOLICITUD_TIPOS.FINALIZADA].includes(record.status)) {
      openDowloadMantencion(record);
    } else {
      addSolicitudToDay(record, selectedDay);
    }
  };

  useEffect(() => {
    if (!hasCalled) {
      call();
    }
  }, [hasCalled, call]);

  const [open, setOpen] = useState(false);
  const closeModal = () => {
    setOpen(false);
  };

  // Estado para el término de búsqueda
  const [searchText, setSearchText] = useState('');

  // Filtrar los datos basados en el término de búsqueda
  const filteredData = useMemo(() => {
    if (!searchText) return data;

    return data.filter((solicitud) =>
      solicitud.Cliente?.name.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [data, searchText]);

  // Removed unused clientNames logic

  const columns = [
    {
      title: 'Estado',
      dataIndex: 'status',
      width: 80,
      render: (estado) => (
        <Tag color={getColorForStatus(estado)} size="small" className="text-xs font-medium">
          {estado?.toUpperCase()}
        </Tag>
      ),
      filters: SOLICITUD_TIPOS_ARRAY.map((estado) => ({
        value: estado,
        text: estado,
      })),
      onFilter: (value, record) => record.status === value,
      defaultFilteredValue: [SOLICITUD_TIPOS.SIN_ASIGNAR],
    },
    {
      title: 'Cliente',
      dataIndex: 'Cliente',
      render: (data) => (
        <div className="min-w-0">
          <div className="font-medium text-sm text-gray-900 truncate">{data?.name}</div>
          <div className="text-xs text-gray-500 truncate">{data?.rut}</div>
          <div className="text-xs text-gray-400 truncate">
            {data?.address}, {data?.commune}
          </div>
        </div>
      ),
      width: 200,
    },
    {
      title: 'Acciones',
      dataIndex: 'actions',
      width: 120,
      render: (_, record) => (
        <Button
          onClick={() => handleClickInSolc(record)}
          type="primary"
          size="small"
          className="text-xs"
        >
          {[SOLICITUD_TIPOS.FINALIZADA, SOLICITUD_TIPOS.ENVIADA].includes(record.status) ? (
            <>
              <DownloadOutlined /> DESCARGAR
            </>
          ) : (
            <>
              <PlusOutlined /> AGREGAR
            </>
          )}
        </Button>
      ),
    },
  ];

  return (
    <>
      <Modal open={open} onCancel={closeModal} footer={false}></Modal>

      {/* Header compacto con buscador */}
      <div className="bg-white rounded-lg shadow-sm border mb-4">
        <div className="px-4 py-3 border-b bg-gray-50">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-medium text-gray-900">Solicitudes de Mantenimiento</h3>
            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">
              {filteredData?.length || 0} solicitudes
            </span>
          </div>
        </div>

        <div className="p-4">
          <Input
            placeholder="Buscar por nombre de cliente..."
            allowClear
            prefix={<SearchOutlined className="text-gray-400" />}
            size="small"
            onChange={(e) => setSearchText(e.target.value)}
            value={searchText}
            className="w-full max-w-md"
          />
        </div>
      </div>

      {/* Tabla compacta */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <Table
          loading={isLoading}
          size="small"
          className="compact-table"
          scroll={{ x: 400 }}
          dataSource={filteredData}
          columns={columns}
          rowKey={SOLICITUDES_ROW_KEY}
          pagination={{
            pageSize: 5,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} solicitudes`,
            pageSizeOptions: ['10', '20', '50'],
          }}
          locale={{
            emptyText: 'No hay solicitudes disponibles',
          }}
        />
      </div>
    </>
  );
};

export default SolicitudesTable;
